import {Autocomplete,Button,DialogTitle,Paper,TextField,Typography} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import Grid from "@mui/material/Grid2";
import { Stack } from "@mui/system";
import { useContext, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setSelectedTemplateType, setForecastStatus, setForecastNote, setAllFieldsFilled, setMonthlyData } from '../../../../../features/forecastersSlice';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { setAllReviewed } from "../../../../../features/forecastersSlice";
import SharedStateContext from "../../../../SharedStateProvider";
import { selectUserWebsitePermissions } from '../../../../../features/userSlice';

const CreateNewForeCasterBase = ({ onViewCreateTable,loading }: any) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const [tempSelectedType, setTempSelectedType] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleCreateClose } = useContext(SharedStateContext);
  const predictionFocusList = [
    { label: "FTD", value: 1 },
    { label: "Growth", value: 2 },
    { label: "Profit", value: 3 },
  ];

  const handleClose = () => {
    setOpen(false);
    setTempSelectedType(null);
  };

  const handleCancelClick = () => {
    // First update the states
    dispatch(setSelectedTemplateType(null));
    dispatch(setAllReviewed(false));
    dispatch(setForecastStatus(null));
    dispatch(setForecastNote(""));
    dispatch(setAllFieldsFilled(false));

    // Then handle navigation with clean URL
    const params = new URLSearchParams(location.search);
    params.delete('predictionFocus');
    handleCreateClose();

    // Navigate once at the end
    const finalUrl = `/assets/overview/forecaster?${params.toString()}`;
    navigate(finalUrl, { replace: true });
  };

  const resetStates = () => {
    dispatch(setSelectedTemplateType(null));
    dispatch(setAllReviewed(false));
    dispatch(setForecastStatus(null));
    dispatch(setForecastNote(""));
    dispatch(setAllFieldsFilled(false));
  };

  useEffect(() => {
    // Listen for navigation/unmount
    const unblock = () => {
      const params = new URLSearchParams(location.search);
      if (params.has('predictionFocus')) {
        params.delete('predictionFocus');
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      }
      resetStates();
    };

    window.addEventListener('beforeunload', unblock);

    return () => {
      window.removeEventListener('beforeunload', unblock);
      resetStates();
    };
    // eslint-disable-next-line
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    if (
      location.pathname === "/assets/overview/forecaster/Createnewforecast" &&
      newSearchParams.get("page") !== "0"
    ) {
      newSearchParams.set("page", "0");
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const predictionFocus = params.get('predictionFocus');
    if (predictionFocus && !selectedTemplateType) {
      const focusValue = parseInt(predictionFocus);
      if ([1, 2, 3].includes(focusValue)) {
        dispatch(setSelectedTemplateType(focusValue));
        onViewCreateTable();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const predictionFocus = params.get('predictionFocus');

    if (predictionFocus) {
      const focusValue = parseInt(predictionFocus);
      if ([1, 2, 3].includes(focusValue)) {
        dispatch(setSelectedTemplateType(focusValue));
      } else {
        params.delete('predictionFocus');
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        dispatch(setSelectedTemplateType(null));
      }
    } else {
      dispatch(setSelectedTemplateType(null));
    }
  }, [location.search, dispatch, location.pathname, navigate]);

  const handlePredictionFocusChange = (newValue: any) => {
    if (newValue) {
      if (selectedTemplateType) {
        setTempSelectedType(newValue);
        setOpen(true);
      } else {
        const updatedParams = new URLSearchParams(location.search);
        updatedParams.set('predictionFocus', newValue.value.toString());
        navigate(`${location.pathname}?${updatedParams.toString()}`);
        dispatch(setSelectedTemplateType(newValue.value));
        onViewCreateTable();
       dispatch(setMonthlyData([]));
      }
    } else {
      dispatch(setSelectedTemplateType(null));
      const updatedParams = new URLSearchParams(location.search);
      updatedParams.delete('predictionFocus');
      navigate(`${location.pathname}?${updatedParams.toString()}`, { replace: true });
      dispatch(setAllReviewed(false));
      dispatch(setMonthlyData([]));
    }
  };

  const handleConfirmChange = () => {
    if (tempSelectedType) {
      const updatedParams = new URLSearchParams(location.search);
      updatedParams.set('predictionFocus', tempSelectedType.value.toString());
      navigate(`${location.pathname}?${updatedParams.toString()}`);
      dispatch(setSelectedTemplateType(tempSelectedType.value));
      onViewCreateTable();
    }
    handleClose();
  };

  const [searchParams] = useSearchParams();
  const selectedYear = searchParams.get("selectedYear") as any;
  const selectedTemplateType = searchParams.get("predictionFocus") as any;
  const websitePermissions = useSelector(selectUserWebsitePermissions) as any;
  const selectedSite = searchParams.get("selectedSite") as any;
  const forecastNote = useSelector((state: any) => state?.forecasters?.forcastNote);

  return (
    <>
      <Paper sx={{ padding: "8px 16px" }}>
        <Grid container spacing={2} alignItems={"center"} mb={2}>
          <Grid>
            <Stack flexDirection={"row"} alignItems={"center"} gap={2}>
              <Typography variant="h5" fontWeight={400}>
                Create New Forecast
              </Typography>
            </Stack>
          </Grid>
          <Grid>
            <Grid container spacing={2}>
              <Grid size={4}>
                <Stack flexDirection={"row"} gap={0.5}>
                  <Typography fontWeight={500}>Asset:</Typography>
                  <Typography fontWeight={400}>
                    {websitePermissions.find((site: any) =>
                      Object.keys(site)[0] === selectedSite
                    )?.[selectedSite]?.ui_site_name}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          spacing={2}
          sx={{ whiteSpace: "nowrap" }}
          mt={1}
          justifyContent={"space-between"}
        >
          <Grid size={8} alignItems={"center"} flexDirection={"row"}>
            <Grid container spacing={2}>
              {/* Year */}
              <Grid size={2}>
                <Typography variant="body2" color="textSecondary">
                  Year
                </Typography>
                <Typography variant="body1">{selectedYear}</Typography>
              </Grid>

              {/* Version */}
              <Grid size={1}>
                <Typography variant="body2" color="textSecondary">
                  Version
                </Typography>
                <Typography variant="body1">-</Typography>
              </Grid>

              {/* Prediction Focus */}
              <Grid size={3}>
                <Autocomplete
                  key={`prediction-focus-${selectedTemplateType || 'empty'}`}
                  size="small"
                  disablePortal
                  options={predictionFocusList}
                  getOptionLabel={(option) => option.label}
                  onChange={(_, newValue) => handlePredictionFocusChange(newValue)}
                  value={selectedTemplateType ? predictionFocusList.find((option) => option.value === Number(selectedTemplateType)) : null}
                  clearOnBlur={false}
                  clearOnEscape
                  disableClearable={false}
                  renderInput={(params) => (
                    <TextField
                      color="secondary"
                      {...params}
                      label="Prediction Focus"
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Note */}
              <Grid size={4}>
                <TextField
                  fullWidth
                  color="secondary"
                  label="Note"
                  value={forecastNote}
                  onChange={(e) => dispatch(setForecastNote(e.target.value))}
                  variant="outlined"
                  size="small"
                  disabled={!selectedTemplateType}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Button */}
          <Grid>
            <Stack direction="row" spacing={2}>
              <Button color="secondary" disabled={loading} variant="text">
                <Stack onClick={handleCancelClick}>Cancel</Stack>
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                data-tracker="130"
                onClick={() => dispatch(setForecastStatus('draft'))}
                disabled={!selectedTemplateType || loading}
              >
                Save as draft
              </Button>
              <Button
                variant="contained"
                color="secondary"
                endIcon={<SaveIcon />}
                data-tracker="130"
                onClick={() => dispatch(setForecastStatus('publish'))}
                disabled={!selectedTemplateType || loading}
              >
                Save & Publish
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      {/* Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle sx={{ color: "#000000DE" }}>
          {"Are you sure you want to change the prediction focus?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText fontWeight={500} sx={{ color: "#000000DE" }}>
            Changing the prediction focus will alter the required the input
            fields and discard all data you have previously reviewed and edited.
            Are you sure you want to change the prediction focus?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px 20px 24px" }}>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            cancel
          </Button>
          <Button
            onClick={handleConfirmChange}
            color="secondary"
            variant="contained"
            autoFocus
          >
            change focus
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateNewForeCasterBase;
